import { useEffect, useRef } from "react";
import type { WebsiteLink } from "./WebsiteGrid";
import { Layers, Languages, ExternalLink, Map, MapPin, User, X } from "lucide-react";

interface MapDetailModalProps {
  map: WebsiteLink;
  onClose: () => void;
}

export function MapDetailModal({ map, onClose }: MapDetailModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    const originalTouchAction = document.body.style.touchAction;
    
    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';
    
    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.touchAction = originalTouchAction;
    };
  }, []);

  useEffect(() => {
    previousFocusRef.current = document.activeElement as HTMLElement;
    
    if (modalRef.current) {
      modalRef.current.focus();
    }
    
    return () => {
      if (previousFocusRef.current) {
        previousFocusRef.current.focus();
      }
    };
  }, []);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  useEffect(() => {
    const handleFocusTrap = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || !modalRef.current) return;

      const focusableElements = modalRef.current.querySelectorAll(
        'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0] as HTMLElement;
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

      if (event.shiftKey) {
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement?.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement?.focus();
        }
      }
    };

    document.addEventListener('keydown', handleFocusTrap);
    return () => document.removeEventListener('keydown', handleFocusTrap);
  }, []);

  return (
    <div 
      className="fixed inset-0 bg-black/90 flex items-center justify-center z-51 p-4"
      onClick={onClose}
    >
        <div ref={modalRef} tabIndex={-1} className="relative max-h-[90dvh] flex flex-col pb-[20px] focus:outline-none">
      <div         
        className="border border-white/30 rounded-[15px] max-w-2xl w-full flex-shrink overflow-y-auto [overscroll-behavior:contain] focus:outline-none z-[52] max-h-full"
        style={{
          background: 'radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.07) 0%, transparent 50%), linear-gradient(to bottom, #0d111d, #141a28)'
        }}
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 pb-4">
          <h2 id="modal-title" className="font-bold text-white text-xl">{map.title}</h2>
        </div>

        {/* Content */}
        <div className="px-6 pb-6">
          <div className="space-y-4">
            {map.description && (
              <div>
                <p className="text-white">{map.description}</p>
              </div>
            )}

            <div>
              <p className="text-white/85 text-sm flex items-center gap-2">
                <MapPin className="w-4 h-4 flex-shrink-0" />
                {(() => {
                  // Check if this is a world map (no location data)
                  const hasLocationData = map.continent || 
                    (map.countries && map.countries.length > 0) || 
                    (map.states && map.states.length > 0) || 
                    (map.districts && map.districts.length > 0) || 
                    (map.cities && map.cities.length > 0);
                  
                  if (!hasLocationData) {
                    return 'Global';
                  }
                  
                  const parts = [];
                  if (map.cities && map.cities.length > 0) {
                    parts.push(map.cities.join(', ').replace(/ /g, '\u00A0'));
                  }
                  if (map.districts && map.districts.length > 0) {
                    parts.push(map.districts.join(', ').replace(/ /g, '\u00A0'));
                  }
                  if (map.states && map.states.length > 0) {
                    parts.push(map.states.join(', ').replace(/ /g, '\u00A0'));
                  }
                  if (map.countries && map.countries.length > 0) {
                    parts.push(map.countries.join(', ').replace(/ /g, '\u00A0'));
                  }
                  if (map.continent) {
                    parts.push(map.continent.replace(/ /g, '\u00A0'));
                  }
                  return parts.join(' • ');
                })()}
              </p>
            </div>

            <div>
              <div className="flex items-start gap-2">
                <Layers className="w-4 h-4 text-white/85 flex-shrink-0 mt-1" />
                <div className="flex flex-wrap gap-2">
                  {map.categories.map((category, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center bg-white/5 border border-white/10 px-3 py-1 rounded-[8px] text-white/85 text-sm"
                    >
                      {category}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Owner row */}
            {map.owner && map.owner.length > 0 && (
              <div>
                <p className="text-white/85 text-sm flex items-center gap-2">
                  <User className="w-4 h-4 flex-shrink-0" />
                  <span className="flex flex-wrap gap-x-1 items-center">
                    {map.owner.map((ownerItem, index) => (
                      <span key={index}>
                        {ownerItem.url ? (
                          <a 
                            href={ownerItem.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-white/85 hover:text-white underline decoration-white/50 transition-colors"
                          >
                            {ownerItem.name}
                          </a>
                        ) : (
                          <span className="text-white/85">
                            {ownerItem.name}
                          </span>
                        )}
                        {index < map.owner.length - 1 && ", "}
                      </span>
                    ))}
                  </span>
                </p>
              </div>
            )}

            {(map.language || map.mapProvider) && (
              <div className="flex gap-6">
                {map.language && (
                  <div className="flex-1">
                    <p className="text-white/85 text-sm flex items-center gap-2">
                      <Languages className="w-4 h-4 flex-shrink-0" />
                      {map.language}
                    </p>
                  </div>
                )}
                {map.mapProvider && (
                  <div className="flex-1">
                    <p className="text-white/85 text-sm flex items-center gap-2">
                      <Map className="w-4 h-4 flex-shrink-0" />
                      {map.mapProvider}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Button */}
            <div className="flex gap-3">
              <a
                href={map.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center justify-center gap-2 flex-1 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 rounded-lg px-6 py-2 text-white transition-all"
              >
                View Map
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </div>
          </div>
        </div>  
         
      </div>
       {/* Floating close indicator below modal */}
        <button
         type="button"
         onClick={onClose}
         className="absolute top-[calc(100%-10px)] left-1/2 -translate-x-1/2 w-20 h-6 rounded-[8px] hover:brightness-125 backdrop-blur-sm flex items-center justify-center cursor-pointer transition-all"
         style={{
            background: 'linear-gradient(to bottom, #12161f, #181e2d)'
          }}
          aria-label="Close modal"
        >
          <X className="w-5 h-5 text-white/70" />
        </button>
      </div>

    </div>
  );
}
        